/* Coverage:
 Statements: 0%
 Branches: 0%
 Functions: 0%
 (Generated by scripts/generate-coverage-comments.js - Do not edit this comment)
*/
import { Button, Card, CardBody, CardHeader, Form, Input } from '@heroui/react'
import { useFeature } from 'flagged'
import { useForm } from 'react-hook-form'
import { ActionFunctionArgs, data, Link, redirect, useNavigation, useParams, useSubmit } from 'react-router'
import { ActionToasts } from '~/components/ActionToasts'
import { supabaseClient } from '~/config/supabase.server'
import useStrings from '~/hooks/useStrings'
import { commitSession, getSession } from '~/services/session.server'
import { stringService } from '~/services/StringService.server'

export async function action({ request, params }: ActionFunctionArgs) {
  console.log('action')
  const session = await getSession(request)
  const formData = await request.formData()
  const email = formData.get('email') as string
  const password = formData.get('password') as string
  const lang = params.lang || 'en'
  const { data: sessionData, error } = await supabaseClient.auth.signInWithPassword({
    email,
    password,
  })
  if (error || !sessionData.session) {
    const errorMessage = stringService.translateRequest('Unable to login with that email and password', request)
    return data(
      {
        error: errorMessage,
      },
      {
        status: 401,
      },
    )
  }
  session.set('accessToken', sessionData.session.access_token)
  session.set('refreshToken', sessionData.session.refresh_token)

  // Redirect to the home page with the current language
  return redirect(`/${lang}`, {
    headers: {
      'Set-Cookie': await commitSession(session),
    },
  })
}
interface LoginFormData {
  email: string
  password: string
}
const Login = () => {
  const navigation = useNavigation()
  const { t } = useStrings()
  const { lang, confirmed } = useParams()
  const v1 = useFeature('v1')
  const isSubmitting = navigation.state === 'submitting'
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormData>({
    mode: 'onChange',
  })
  const submit = useSubmit()

  const onSubmit = (event: React.FormEvent) => {
    if (isSubmitting) return
    handleSubmit(() => {
      submit(event.target as HTMLFormElement)
      return undefined
    })(event)
  }
  if (!v1) {
    return <div>Coming soon</div>
  }
  return (
    <div className="w-full flex flex-col gap-4 items-center justify-center">
      {confirmed && (
        <h1 className="text-primary dark:text-white w-fit mx-auto">
          {t('Thank you - email confirmed. You may login and start practicing now.')}
        </h1>
      )}
      <Card className="mb-4 p-6 max-w-lg w-full">
        <CardHeader>
          <h1 className="text-xl text-secondary w-fit mx-auto">{t('Login To Your Account')}</h1>
        </CardHeader>
        <CardBody>
          <ActionToasts />
          <Form method="post" className="space-y-6" aria-label={t('Sign in form')} onSubmit={onSubmit}>
            <Input
              {...register('email', {
                required: t('Email is required'),
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: t('Enter a valid email address'),
                },
              })}
              name="email"
              fullWidth
              type="email"
              label={t('Email')}
              labelPlacement="outside"
              isInvalid={!!errors.email}
              errorMessage={errors.email?.message}
            />
            <Input
              {...register('password', {
                required: t('Password is required'),
              })}
              name="password"
              fullWidth
              type="password"
              label={t('Password')}
              labelPlacement="outside"
              isInvalid={!!errors.password}
              errorMessage={errors.password?.message}
            />
            <div className="flex items-center justify-between">
              <Link
                to={`/${lang || 'en'}/forgot-password`}
                className="text-xs text-secondary"
                aria-label={t('Forgot password?')}
              >
                {t('Forgot password?')}
              </Link>
            </div>
            <Button
              type="submit"
              color="primary"
              isDisabled={isSubmitting || !isValid}
              className="w-1/2 mx-auto"
              aria-label={t('Log In')}
              aria-disabled={isSubmitting || !isValid}
            >
              {t('Log In')}
            </Button>
          </Form>
          <div className="flex items-center justify-center mt-6 text-xs space-x-2" role="contentinfo">
            <span className="text-secondary">{t(`Don't have an account?`)}</span>
            <Link
              className="navigate-link text-secondary"
              to={`/${lang || 'en'}/register`}
              aria-label={t('Register for a new account')}
            >
              {t('Register')}
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
export default Login
